import { isFunction, merge } from 'lodash';
import { UiActions } from '@app/store/actions/ui/ui.actions';
import { MAX_PRINT_OR_EXPORT_SIZE } from '@config/global-constants';
import { NotificationType } from '../types/ui/components/notification.type';
import { TOASTER_ERROR_MESSAGE } from '../constants/editor-messages';
import { IBaseEntityType } from '../types/entities/base/base.type';
import { XVII18nService } from '@app/i18n/services';
import { I18n } from '@app/i18n/strings-keys';

type PredicateIsExport = (...params: any[]) => boolean;

interface BulkPrintExportPromptOptions {
  uiActions: UiActions;

  /**
   * is export pdf
   */
  isExport: boolean | PredicateIsExport;
  /**
   * max print or export pdf count
   */
  maxPrintOrExportSize?: number;

  i18nService: XVII18nService;
}

const withBulkPrintPrompt = (
  printOrExportFunc: (selectedItems: IBaseEntityType[]) => Promise<void>,
  options: BulkPrintExportPromptOptions,
): ((selectedItems: IBaseEntityType[]) => Promise<void>) => {
  const i18nService = options.i18nService;

  const defaultOptions: Partial<BulkPrintExportPromptOptions> = {
    maxPrintOrExportSize: MAX_PRINT_OR_EXPORT_SIZE,
  };

  let opts: BulkPrintExportPromptOptions;

  const isExport = isFunction(options?.isExport)
    ? options?.isExport(options)
    : options?.isExport;
  // eslint-disable-next-line prefer-const
  opts = merge({}, defaultOptions, options, { isExport });

  return async (...printOrExportFuncParams: any[]): Promise<any> => {
    const [selectedItems] = printOrExportFuncParams;

    if (selectedItems.length > opts.maxPrintOrExportSize) {
      // eslint-disable-next-line no-invalid-this
      opts.uiActions.showToasterMessage({
        type: NotificationType.ERROR,
        title: TOASTER_ERROR_MESSAGE.title,
        body: i18nService.t(
          I18n.ErrorMessages.YouCanOnlyOperateUpToMaxCountRecords,
          {
            operate: i18nService.t(
              opts.isExport ? I18n.Common.export : I18n.Common.print,
            ),
            maxCount: opts.maxPrintOrExportSize,
          },
        ),
      });

      return;
    }

    try {
      // eslint-disable-next-line no-invalid-this
      await printOrExportFunc.bind(this)(...printOrExportFuncParams);
    } catch (e) {
      opts.uiActions.disableLoader();
      opts.uiActions.showToasterMessage({
        type: NotificationType.ERROR,
        title: TOASTER_ERROR_MESSAGE.title,
        body: I18n.ErrorMessages.CheckInternetAndTryAgain,
      });
    }
  };
};

export { withBulkPrintPrompt, BulkPrintExportPromptOptions, PredicateIsExport };
